/**
 * Entra ID sign-in for the SPA.
 *
 * The browser cannot know whether to sign in until the API tells it, so the
 * first thing that happens on load is an anonymous call to /api/auth/config.
 * That keeps one built artifact working both locally (dev header, no tenant)
 * and in Azure (real tokens), with no rebuild and no environment variables
 * baked into the bundle.
 *
 * The app never holds a database credential: the user signs in, the browser
 * gets a token for the API, and the API reaches SQL with its own identity.
 */
import {
  InteractionRequiredAuthError,
  PublicClientApplication,
  type AccountInfo,
} from '@azure/msal-browser';
import { setTokenProvider } from './client';

export interface AuthConfig {
  devMode: boolean;
  tenantId?: string | null;
  clientId?: string | null;
  /** api://<client-id>/access_as_user — the scope that gets a token for our API. */
  scope?: string | null;
  /** Where each Cyntox app lives, keyed "scorecard", "admin", … for the links between them. */
  apps?: Record<string, string>;
}

let msal: PublicClientApplication | null = null;
let account: AccountInfo | null = null;
let loadedConfig: AuthConfig | null = null;

/** The configuration the API sent at start-up. Null before initAuth has run. */
export function getAuthConfig(): AuthConfig | null {
  return loadedConfig;
}

/**
 * Resolves once the app may render. In development that is immediately. With
 * Entra it is after a token is available — or never, because signing in
 * navigates away to Microsoft and this page is replaced.
 */
export async function initAuth(): Promise<AuthConfig> {
  const res = await fetch('/api/auth/config');
  if (!res.ok) throw new Error(`The API answered ${res.status} when asked how to sign in.`);
  const config: AuthConfig = await res.json();
  loadedConfig = config;

  // Development, or a deployment with no tenant configured yet: the dev-user
  // header carries the identity and MSAL never loads.
  if (config.devMode || !config.clientId || !config.tenantId) return config;

  const scope = config.scope ?? `api://${config.clientId}/access_as_user`;

  msal = new PublicClientApplication({
    auth: {
      clientId: config.clientId,
      authority: `https://login.microsoftonline.com/${config.tenantId}`,
      // Whatever origin we are on — one registration covers 127.0.0.1,
      // localhost and the Azure URL, provided each is a registered SPA URI.
      redirectUri: window.location.origin,
    },
    cache: {
      // Session rather than local: a token is gone when the browser closes.
      // Re-entry is still silent, because the Entra session cookie survives.
      cacheLocation: 'sessionStorage',
    },
  });

  await msal.initialize();

  // Completes a sign-in we started before being redirected away. Null on an
  // ordinary load.
  const redirect = await msal.handleRedirectPromise();
  account = redirect?.account ?? msal.getAllAccounts()[0] ?? null;

  if (!account) {
    // Leaves the page. Nothing after this runs.
    await msal.loginRedirect({ scopes: [scope] });
    return config;
  }

  msal.setActiveAccount(account);

  setTokenProvider(async () => {
    if (!msal || !account) return null;
    try {
      const result = await msal.acquireTokenSilent({ scopes: [scope], account });
      return result.accessToken;
    } catch (err) {
      // The silent path fails when consent is needed or the session expired.
      // Anything else is a real error and should surface rather than loop.
      if (err instanceof InteractionRequiredAuthError) {
        await msal.acquireTokenRedirect({ scopes: [scope], account });
        return null;
      }
      throw err;
    }
  });

  return config;
}

/** Who is signed in, for the sidebar. Null in development. */
export function signedInName(): string | null {
  return account?.name ?? account?.username ?? null;
}

export async function signOut(): Promise<void> {
  if (!msal) return;
  await msal.logoutRedirect({ account: account ?? undefined });
}
